import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader, Card, Btn } from "../components/UI";
import { Sparkles, SlidersHorizontal, LayoutTemplate, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

const TEMPLATES = [
  { key: "candidate_awareness", label: "Candidate Awareness", prompt: "Create a campaign to introduce our candidate across the district." },
  { key: "political_ctv", label: "Political CTV", prompt: "Create a CTV-first campaign to reach voters in our district." },
  { key: "reach_district", label: "Reach My District", prompt: "Reach as many voters as possible in our district over the next 30 days." },
  { key: "issue_advocacy", label: "Issue Advocacy", prompt: "Create a campaign to explain our position on a key issue to voters." },
  { key: "ballot_measure", label: "Ballot Measure", prompt: "Create CTV and digital advertising for our ballot initiative." },
  { key: "fundraising_promo", label: "Fundraising Promotion", prompt: "Promote our fundraising page with a $20,000 advertising budget." },
  { key: "rapid_response", label: "Rapid Response", prompt: "Create a seven-day rapid response campaign around our approved public safety message using $20,000." },
  { key: "voter_education", label: "Voter Education", prompt: "Create a voter education campaign about how and when to vote in our district." },
  { key: "ctv_digital", label: "CTV + Digital", prompt: "Create a CTV plus digital campaign for the next 30 days." },
  { key: "online_video", label: "Online Video", prompt: "Create an online video campaign to increase video exposure for our candidate." },
  { key: "supporter_retargeting", label: "Supporter Retargeting", prompt: "Retarget our website visitors and supporters with follow-up messaging." },
  { key: "first_party", label: "First-Party Voter File", prompt: "Use our voter file and reach those voters on CTV and online video." },
  { key: "final_push", label: "Final Election Push", prompt: "Create a final election push campaign from now until Election Day." },
];

export default function CreateCampaignChoice() {
  const navigate = useNavigate();
  const [starting, setStarting] = useState("");

  const startAgent = async (template) => {
    setStarting(template?.key || "ai");
    try {
      const r = await api.post("/agent/sessions", { template: template?.key || null });
      navigate(`/campaign-agent/${r.data.id}`, { state: { prefill: template?.prompt || "" } });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start the Campaign Agent");
      setStarting("");
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto" data-testid="create-campaign-choice">
      <PageHeader
        eyebrow="Create Campaign"
        title="How would you like to create your campaign?"
        subtitle="Political advertising without DSP complexity — tell TotalReach what you want to accomplish, or take full control in Pro Mode."
      />

      <div className="grid md:grid-cols-2 gap-4 mb-8">
        <Card className="relative border-cyan-400/30 bg-gradient-to-br from-cyan-400/10 to-transparent" data-testid="create-with-ai-card">
          <span className="absolute top-4 right-4 text-[10px] px-2 py-0.5 rounded-full bg-cyan-400/20 border border-cyan-400/30 text-cyan-300 uppercase tracking-wider font-semibold">Recommended</span>
          <div className="w-11 h-11 rounded-xl bg-cyan-400/15 border border-cyan-400/25 flex items-center justify-center mb-4">
            <Sparkles className="w-5 h-5 text-cyan-300" />
          </div>
          <h3 className="font-display text-xl font-bold">Create With AI</h3>
          <p className="text-sm text-slate-400 mt-2 leading-relaxed">
            Tell TotalReach what you want to accomplish. We'll find the audience, recommend the media plan — CTV first — set the budget, and build the campaign for you.
          </p>
          <Btn className="mt-5" onClick={() => startAgent(null)} disabled={!!starting} data-testid="start-ai-campaign-btn">
            {starting === "ai" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Start AI Campaign
          </Btn>
        </Card>

        <Card data-testid="pro-mode-card">
          <div className="w-11 h-11 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center mb-4">
            <SlidersHorizontal className="w-5 h-5 text-slate-300" />
          </div>
          <h3 className="font-display text-xl font-bold">Build It Myself</h3>
          <p className="text-sm text-slate-400 mt-2 leading-relaxed">
            Configure audiences, media channels, geography, budgets, creatives and advanced settings yourself in the seven-step Pro Mode builder.
          </p>
          <Btn variant="secondary" className="mt-5" onClick={() => navigate("/campaigns/new/pro")} data-testid="open-pro-mode-btn">
            <SlidersHorizontal className="w-4 h-4" /> Open Pro Mode
          </Btn>
        </Card>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <LayoutTemplate className="w-4 h-4 text-cyan-300" />
        <h3 className="font-display text-lg font-bold">Start From Template</h3>
        <span className="text-xs text-slate-500">— each template opens the AI agent with a head start</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="template-grid">
        {TEMPLATES.map((t) => (
          <button key={t.key} onClick={() => startAgent(t)} disabled={!!starting} data-testid={`template-${t.key}`}
            className="group text-left rounded-lg border border-white/10 bg-white/[0.03] hover:border-cyan-400/30 hover:bg-cyan-400/5 transition-colors p-4">
            <div className="text-sm font-medium flex items-center justify-between">
              {t.label}
              {starting === t.key ? <Loader2 className="w-3.5 h-3.5 animate-spin text-cyan-300" /> : <ArrowRight className="w-3.5 h-3.5 text-slate-600 group-hover:text-cyan-300 transition-colors" />}
            </div>
            <p className="text-[11px] text-slate-500 mt-1.5 line-clamp-2">{t.prompt}</p>
          </button>
        ))}
      </div>
    </div>
  );
}
